// ADR-091 Phase 5b.2 — async query jobs API client.
//
// Two endpoints, mirroring the Phase 3 SSE relay design:
//
//   POST  /v1/tenants/{tid}/query/jobs            → 202 + {job_id, links}
//   GET   /v1/tenants/{tid}/query/jobs/{id}/stream → SSE relay
//
// The relay endpoint emits the SAME StreamEvent payload shape as the
// sync POST /v1/tenants/{tid}/query/stream because the worker (per
// ADR-091 Phase 5b backend) passes `event.model_dump(mode="json")`
// straight through into the PG/Redis Stream event log. That means the
// existing parseFrame + store.applyEvent dispatcher work unchanged —
// every per-token, per-tool, per-citation StreamEvent reaches the
// Zustand store with the correct discriminated-union type.
//
// We use fetch + ReadableStream (NOT EventSource) because EventSource
// doesn't support custom headers, and we need Bearer auth.

import type { JobListResponse, JobSnapshot, JobStatus, StreamEvent } from "@/types";
import { API_BASE_URL, apiFetch } from "./client";

export interface JobCreateArgs {
  tenantId: string;
  text: string;
  topK?: number;
  includeTrace?: boolean;
  conversationId?: string;
}

export interface JobCreateResponse {
  job_id: string;
  status: string;
  created_at: string;
  links: {
    self: string;
    cancel: string;
    stream: string;
  };
}

export interface JobStreamHandlers {
  onEvent: (event: StreamEvent) => void;
  onError?: (err: unknown) => void;
  onClose?: () => void;
  onActivity?: () => void;
  // ADR-091 Phase 3 Last-Event-ID resumption: fired before each
  // reconnect attempt (attempt counts from 1; lastEventId is the
  // highest sequence_id observed so far). Optional — chat-ui can
  // ignore and the reconnect still happens silently. Future UX work
  // can surface a "reconnecting..." inline note from this handler.
  onReconnect?: (attempt: number, lastEventId: number) => void;
}

// Exponential backoff before each reconnect attempt. Lengths picked so
// the user sees at most ~15.5s of silent retry before the budget
// (5 attempts) is exhausted — long enough to survive a Container App
// revision swap (~5-10s) but short enough that a permanent failure
// surfaces in under 20s.
const RECONNECT_BACKOFF_MS: ReadonlyArray<number> = [500, 1000, 2000, 4000, 8000];
const MAX_RECONNECTS = RECONNECT_BACKOFF_MS.length;

export class JobCreateError extends Error {
  readonly status: number;
  readonly detail: string;
  constructor(status: number, detail: string) {
    super(`POST /query/jobs HTTP ${status}: ${detail}`);
    this.status = status;
    this.detail = detail;
  }
}

export async function getJob(
  tenantId: string,
  jobId: string,
  token: string,
): Promise<JobSnapshot> {
  // Iter 19 — snapshot-based terminal-job history (closes iter 18 defect 2
  // structurally). ADR-091 Phase 1 endpoint: GET /v1/tenants/{tid}/query/jobs/
  // {job_id}. Backend returns 404 on missing-OR-cross-user (anti-enumeration
  // per app/routes/query_jobs.py:_gate_request). chatStore.loadJobFromHistory
  // uses this to discriminate terminal vs active: terminal → hydrate from
  // snapshot (NO SSE replay), active → existing pumpJobReplay path.
  const path = `/v1/tenants/${encodeURIComponent(tenantId)}/query/jobs/${encodeURIComponent(jobId)}`;
  return apiFetch<JobSnapshot>(path, token);
}

export async function listJobs(
  tenantId: string,
  token: string,
  opts?: { limit?: number; status?: JobStatus; includeArchived?: boolean },
): Promise<JobListResponse> {
  const params = new URLSearchParams();
  if (opts?.limit) params.set("limit", String(opts.limit));
  if (opts?.status) params.set("status", opts.status);
  if (opts?.includeArchived === true) params.set("include_archived", "true");
  const qs = params.toString();
  const path = `/v1/tenants/${encodeURIComponent(tenantId)}/query/jobs${qs ? `?${qs}` : ""}`;
  return apiFetch<JobListResponse>(path, token);
}

export async function deleteJob(
  tenantId: string,
  jobId: string,
  token: string,
): Promise<void> {
  // 204 No Content; FK CASCADE on query_job_events clears event log.
  const path = `/v1/tenants/${encodeURIComponent(tenantId)}/query/jobs/${encodeURIComponent(jobId)}`;
  await apiFetch<void>(path, token, { method: "DELETE" });
}

export async function renameJob(
  tenantId: string,
  jobId: string,
  title: string,
  token: string,
): Promise<JobSnapshot> {
  const path = `/v1/tenants/${encodeURIComponent(tenantId)}/query/jobs/${encodeURIComponent(jobId)}`;
  return apiFetch<JobSnapshot>(path, token, { method: "PATCH", body: { title } });
}

export async function archiveJob(
  tenantId: string,
  jobId: string,
  token: string,
): Promise<JobSnapshot> {
  // Idempotent — re-archive returns same archived_at.
  const path = `/v1/tenants/${encodeURIComponent(tenantId)}/query/jobs/${encodeURIComponent(jobId)}/archive`;
  return apiFetch<JobSnapshot>(path, token, { method: "POST" });
}

export async function cancelJob(
  tenantId: string,
  jobId: string,
  token: string,
): Promise<void> {
  // Phase 1 endpoint: POST /v1/tenants/{tid}/query/jobs/{job_id}/cancel.
  // Idempotent — POSTing on a terminal job returns the same snapshot.
  // The worker observes cancel_requested_at at stage boundaries per
  // ADR-091 §3.8 (in-flight LLM calls NOT cancelled because Foundry
  // doesn't support mid-generation cancel; the next event boundary
  // honors the flag).
  const url = `${API_BASE_URL.replace(/\/$/, "")}/v1/tenants/${encodeURIComponent(tenantId)}/query/jobs/${encodeURIComponent(jobId)}/cancel`;
  await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: "{}",
  });
}

export async function startJob(args: JobCreateArgs, token: string): Promise<JobCreateResponse> {
  const url = `${API_BASE_URL.replace(/\/$/, "")}/v1/tenants/${encodeURIComponent(args.tenantId)}/query/jobs`;
  const body = {
    text: args.text,
    top_k: args.topK ?? 20,
    include_trace: args.includeTrace ?? false,
    conversation_id: args.conversationId,
  };
  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(body),
  });
  if (res.status !== 202) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = typeof j?.detail === "string" ? j.detail : JSON.stringify(j);
    } catch {
      // ignore
    }
    if (res.status === 502) {
      // Backend-shipped diagnostic from app/routes/query_jobs.py: the
      // SB publish failed, most likely because rag-query Container App
      // is NOT provisioned. Surface in dev tools without breaking the UI.
      // eslint-disable-next-line no-console
      console.warn(
        "[ADR-091] POST /jobs returned 502 — the row persisted in PG but " +
          "Service Bus publish failed. Operator action: " +
          "bash scripts/deploy_v2.sh --env=dev --service=rag-query.",
      );
    }
    throw new JobCreateError(res.status, detail);
  }
  return (await res.json()) as JobCreateResponse;
}

export async function subscribeJobStream(
  tenantId: string,
  jobId: string,
  token: string,
  handlers: JobStreamHandlers,
  signal?: AbortSignal,
  initialLastEventId?: number,
): Promise<void> {
  let lastEventId = typeof initialLastEventId === "number" && initialLastEventId > 0 ? initialLastEventId : 0;
  let terminal = false;

  for (let attempt = 0; attempt <= MAX_RECONNECTS; attempt += 1) {
    if (signal?.aborted) break;

    if (attempt > 0) {
      handlers.onReconnect?.(attempt, lastEventId);
      const backoffMs = RECONNECT_BACKOFF_MS[attempt - 1] ?? 8000;
      const interrupted = await abortableDelay(backoffMs, signal);
      if (interrupted) break;
    }

    const outcome = await streamOnce(tenantId, jobId, token, lastEventId, signal, (frame) => {
      // ADR-098 Phase-4 watchdog fix: ANY received frame — an event OR a
      // `: keepalive` comment — proves the transport is alive. Signal it so
      // useStreamWatchdog won't endStream a stream merely in a long
      // silent-reasoning phase while the relay's 10s keepalives still flow.
      handlers.onActivity?.();
      const { event, sequenceId } = parseSseFrameWithId(frame);
      if (sequenceId !== null && sequenceId > lastEventId) {
        lastEventId = sequenceId;
      }
      if (event) {
        handlers.onEvent(event);
        if (isTerminalEvent(event)) terminal = true;
      }
    });

    if (outcome === "abort") {
      handlers.onClose?.();
      return;
    }
    if (terminal || outcome === "ended") break;
    // outcome === "transport-error": fall through to retry on next loop iteration.
  }

  if (!terminal && !signal?.aborted) {
    handlers.onError?.({
      message: `Reconnect budget exhausted after ${MAX_RECONNECTS} attempts (last_event_id=${lastEventId})`,
    });
  }
  handlers.onClose?.();
}

type StreamOutcome = "abort" | "ended" | "transport-error";

async function streamOnce(
  tenantId: string,
  jobId: string,
  token: string,
  lastEventId: number,
  signal: AbortSignal | undefined,
  onFrame: (frame: string) => void,
): Promise<StreamOutcome> {
  const url = `${API_BASE_URL.replace(/\/$/, "")}/v1/tenants/${encodeURIComponent(tenantId)}/query/jobs/${encodeURIComponent(jobId)}/stream`;
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    Accept: "text/event-stream",
  };
  // ADR-091 Phase 3 contract: server clamps malformed / negative
  // values to 0, so we only send when we have a real positive seq.
  if (lastEventId > 0) {
    headers["Last-Event-ID"] = String(lastEventId);
  }

  let res: Response;
  try {
    res = await fetch(url, { method: "GET", headers, signal });
  } catch (err) {
    return (err as Error)?.name === "AbortError" ? "abort" : "transport-error";
  }

  if (!res.ok || !res.body) {
    return "transport-error";
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) return "ended";
      buffer += decoder.decode(value, { stream: true });
      for (let idx = buffer.indexOf("\n\n"); idx >= 0; idx = buffer.indexOf("\n\n")) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        onFrame(frame);
      }
    }
  } catch (err) {
    return (err as Error)?.name === "AbortError" ? "abort" : "transport-error";
  }
}

function isTerminalEvent(event: StreamEvent): boolean {
  return event.type === "complete" || event.type === "error";
}

function abortableDelay(ms: number, signal?: AbortSignal): Promise<boolean> {
  // Resolves false on completion, true if aborted mid-wait. Never rejects.
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve(true);
      return;
    }
    const timer = setTimeout(() => resolve(false), ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve(true);
      },
      { once: true },
    );
  });
}

interface SseFrame {
  event: StreamEvent | null;
  sequenceId: number | null;
}

function parseSseFrameWithId(frame: string): SseFrame {
  // Each SSE frame is one or more lines:
  //   id: <sequence_id>
  //   event: <event_type>
  //   data: <json>
  // Keepalive comments (`: keepalive`) are skipped. We extract id: for
  // Last-Event-ID resumption + trust the JSON payload's discriminated
  // `type` field — the worker emits event.model_dump() which preserves
  // type via Pydantic, so the shape is identical to the sync
  // /v1/query/stream wire format.
  let dataLine: string | null = null;
  let idLine: string | null = null;
  for (const rawLine of frame.split("\n")) {
    const line = rawLine.trimEnd();
    if (line.startsWith(":")) continue;
    if (line.startsWith("data: ")) dataLine = line.slice(6);
    else if (line.startsWith("id: ")) idLine = line.slice(4);
  }
  let event: StreamEvent | null = null;
  if (dataLine) {
    try {
      event = JSON.parse(dataLine) as StreamEvent;
    } catch {
      // ignore malformed frame
    }
  }
  let sequenceId: number | null = null;
  if (idLine) {
    const parsed = parseInt(idLine, 10);
    if (Number.isFinite(parsed) && parsed >= 0) sequenceId = parsed;
  }
  return { event, sequenceId };
}
